/**
 * Shows a styled toast (bottom-center, glassy card with an icon, slide + fade
 * animation) used for copy feedback.
 */
export const showCopyFeedback = (message: string, type: "success" | "error" = "success") => {
    const styleId = "dc-toast-keyframes";
    if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent =
            "@keyframes dc-toast-in{from{opacity:0;transform:translate(-50%,14px)}to{opacity:1;transform:translate(-50%,0)}}" +
            "@keyframes dc-toast-out{from{opacity:1;transform:translate(-50%,0)}to{opacity:0;transform:translate(-50%,14px)}}";
        document.head.appendChild(style);
    }

    const toast = document.createElement("div");
    Object.assign(toast.style, {
        position: "fixed",
        left: "50%",
        bottom: "28px",
        transform: "translateX(-50%)",
        zIndex: "2147483647",
        display: "flex",
        alignItems: "center",
        gap: "10px",
        maxWidth: "min(92vw, 440px)",
        padding: "11px 16px",
        borderRadius: "12px",
        background: "rgba(24, 24, 27, 0.9)",
        color: "#fff",
        fontSize: "14px",
        fontWeight: "500",
        lineHeight: "1.3",
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
        border: "1px solid rgba(255, 255, 255, 0.12)",
        boxShadow: "0 12px 40px rgba(0, 0, 0, 0.55)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        animation: "dc-toast-in 220ms cubic-bezier(0.16, 1, 0.3, 1)",
    });

    const badge = document.createElement("span");
    Object.assign(badge.style, {
        display: "inline-flex",
        flex: "0 0 auto",
        width: "20px",
        height: "20px",
        borderRadius: "50%",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        fontWeight: "700",
        background: type === "error" ? "#e5484d" : "#30a46c",
        color: "#fff",
    });
    badge.textContent = type === "error" ? "✕" : "✓";

    const label = document.createElement("span");
    label.textContent = message;

    toast.append(badge, label);
    document.body.appendChild(toast);

    setTimeout(() => {
        toast.style.animation = "dc-toast-out 220ms ease-in forwards";
        setTimeout(() => toast.remove(), 220);
    }, 2400);
};

/**
 * Copies text to the clipboard and shows feedback.
 *
 * Uses the async Clipboard API when available (requires a secure context —
 * https, or localhost/127.0.0.1), and falls back to a hidden-textarea +
 * execCommand('copy') so it still works over plain HTTP (e.g. http://*.lvh.me
 * during local dev) and in older browsers.
 */
const legacyCopy = (text: string): boolean => {
    try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.top = "0";
        textarea.style.left = "0";
        textarea.style.opacity = "0";
        textarea.style.pointerEvents = "none";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        return ok;
    } catch {
        return false;
    }
};

export const copyToClipboard = async (text: string, feedbackMessage: string = "Copied to clipboard!"): Promise<boolean> => {
    // Preferred path: async Clipboard API (secure contexts only).
    if (navigator.clipboard?.writeText) {
        try {
            await navigator.clipboard.writeText(text);
            showCopyFeedback(feedbackMessage);
            return true;
        } catch {
            // Permission denied or non-secure context — fall through to legacy copy.
        }
    }

    if (legacyCopy(text)) {
        showCopyFeedback(feedbackMessage);
        return true;
    }

    console.error("Failed to copy to clipboard");
    showCopyFeedback("Failed to copy to clipboard", "error");
    return false;
};

/** Two-letter avatar fallback from an email's local part, e.g. "rk@..." -> "RK". */
export function emailInitials(email: string | undefined): string {
    if (!email) {
        return "?";
    }
    const local = email.split("@")[0].replace(/[^a-zA-Z]/g, "");
    return (local.slice(0, 2) || email.slice(0, 2)).toUpperCase();
}

/** English like the rest of the dashboard copy: "today", "tomorrow", "in 19 days", "5 minutes ago". */
export const RELATIVE_TIME_FORMAT = new Intl.RelativeTimeFormat("en", {
    numeric: "auto",
});

/**
 * "just now", "5 minutes ago", "12 days ago", "2 months ago". Callers should put the exact
 * timestamp in a title so the precise time is one hover away.
 */
export function formatRelativeTime(value: string | Date, now: Date = new Date()): string {
    const seconds = Math.round((new Date(value).getTime() - now.getTime()) / 1000);
    const abs = Math.abs(seconds);
    const units: [
        Intl.RelativeTimeFormatUnit,
        number,
    ][] = [
        [
            "year",
            365 * 24 * 60 * 60,
        ],
        [
            "month",
            30 * 24 * 60 * 60,
        ],
        [
            "day",
            24 * 60 * 60,
        ],
        [
            "hour",
            60 * 60,
        ],
        [
            "minute",
            60,
        ],
    ];
    for (const [unit, size] of units) {
        if (abs >= size) {
            return RELATIVE_TIME_FORMAT.format(Math.round(seconds / size), unit);
        }
    }
    return "just now";
}
